import { useRouter, useSearchParams } from "next/navigation";
import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { useFormContext } from "react-hook-form";
import { useHandleApiCall } from "@/src/components/pages/StepPage/components/apiCalls/useHandleApiCall";
import { DataTypeEnum } from "@/src/shared/models/contextInstance";
import {
  formatContextVariablesListToFormValues,
  formatFormDataToVarList,
  safeParseJson,
} from "@/src/shared/utils/form";
import { useContextStore } from "@/src/stores/useContextStore";
import { ContextInstanceVariableType, TouristType } from "@/src/shared/schemas";
import {
  has,
  isObject,
  isString,
  last,
  size,
  some,
  sum,
  toNumber,
} from "lodash";
import { parseContextVariableValue } from "@/src/shared/utils/dataTypeHelpers";
import {
  apiCallsSideEffects,
} from "@/src/components/pages/StepPage/components/apiCalls/sideEffects";
import {
  closeErrorModal,
  closeLoadingModal,
  openErrorModal,
  openLoadingModal,
} from "@/src/components/pages/StepPage/components/apiCalls/modalStore";
import { Button as ButtonUI } from "@/src/components/Button";
import {
  scrollToTop,
  useApiCall,
  useNextNavigation,
  usePreviousNavigation,
  useSpecificStepNavigation,
  useUpdateProcessInstance,
} from "@/src/components/Action/useNavigation";
import useToggle from "@/src/hooks/useToggle";
import { useStore } from "zustand";
import {
  COUNTRY_TRAVEL_ZONE_CONST,
  FOR_TOURISTS_CONST,
  INSURED_COUNT_VAR_LIST,
  INSURED_COUNT_VAR_LIST_VISA_CENTRE,
  INSURED_TOURISTS_CONST,
  PICKED_TRAVEL_CALC_CATEGORY_CONST,
  PLACE_OF_STAY_ABROAD_CONST,
  ProposalTableColumnsKeys,
  TERRORISM_CONST,
  TRAVEL_PURPOSE_CONST,
  VisaCentreColumnsKeys,
  WORK_CONST,
} from "../ProposalTable/lib/const";
import { runAction } from "@/src/api/server-actions/action";
import {
  determinePickedCategory,
  getColContextValueString,
} from "../ProposalTable/lib/utils";
import {
  actionRunningStore,
  runBeforeSubmitActions,
} from "@/src/hooks/blockly/useHandleBlocklyFields";
import { proposalSelectionPendingStore } from "@/src/components/ProposalTable/lib/proposalSelectionPendingStore";
import { ApiCallsTypes, leadActionNameMap, TRAVEL_FAMILY_FLOW_CODES } from "../pages/StepPage/components/apiCalls/contants";
import { premiumStore } from "@/src/stores/premiumStore";
import { useAnalytics } from "@/src/components/pages/StepPage/components/useAnalytics/useAnalytics";
import { logTravelAction } from "@/src/api/server-actions/analytics";
import { useUpdateField } from "@/src/hooks/useFieldDebouncedCallback";
import {
  getPlanNameReqData,
  recalcInsuredCounts,
  recalcInsuredCountsVisaCentre,
  useFormDataMiddleware,
} from "./useFormDataMiddleware";
import { flattenContextForLog, getContextVariablesToApiCall } from "@/src/components/Action/utils";
import { useKeysToUpdate } from "@/src/components/Action/useKeysToUpdate";
import { useTimeSpentStore } from "@/src/stores/useTimeSpentStore";
import { useTextUsModalStore } from "@/src/stores/useTextUsModalStore";
import { useMutation } from "@tanstack/react-query";
import { addErrorLog } from "@/src/api/services/integration-log";
import { logError } from "@/src/api/server-actions/analytics";
import { nowInKyiv } from "@/src/shared/utils/dateTime";
import { getSelectOptionValue } from "@/src/shared/utils/formDataMiddleware";
import { checkExistingContracts } from "@/src/components/Action/services/check-existing-contracts";
import { downloadPolicy } from "@/src/components/Action/services/download-policy";
import { checkPremiumLimit } from "@/src/components/Action/services/check-premium-limit";
import { getTravelPremiumMismatch } from "@/src/components/Action/utils/travelPremiumCheck";
import { crossesCheck } from "@/src/components/Action/services/crosses-check";
import { documentsApprove } from "@/src/components/Action/services/documents-approve";
import { ParsedValue } from "@/src/components/TextWithValue";

const getSearchParam = (key: string) => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(key);
};


export function Action(props: any) {
  const {
    methods = [],
    withActionsLoader,
    actionsLoaderTitle,
    actionsLoaderDescription,
    children,
    onClick,
    analytics,
    ...restProps
  } = props;

  const router = useRouter();

  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);

  const { callAnalyticEvent, trackAnalyticEvent } = useAnalytics();

  const updateField = useUpdateField();
  const { isToggled: isLoadingAction, setIsToggled: setLoadingAction } =
    useToggle({ initialState: false });
  const isSelectionPending = useStore(
    proposalSelectionPendingStore,
    (s) => s.isSelectionPending,
  );
  const isActionRunning = useStore(
    actionRunningStore,
    (s) => s.isActionRunning,
  );
  const processInstance = useProcessInstanceStore(
    (state) => state.processInstance,
  );
  const { setPremium } = premiumStore();
  const { handleSubmit, formState, control, setValue, getValues, reset, trigger } =
    useFormContext();
  const { errors } = formState;
  const { handleApiCallWithSideEffects, handleApiCallByName } =
    useHandleApiCall();

  const { formDataMiddleware, cleanTouristListFromShadowItems } =
    useFormDataMiddleware();

  const { getKeysToUpdate } = useKeysToUpdate();

  const { mutateAsync: mutateNext, isPending: isNextLoading } =
    useNextNavigation({
      onSuccess: () => {
        // it will set latest values and just reset dirty and etc.
        reset(getValues());
      },
    });
  const { mutateAsync: mutatePrev, isPending: isPrevLoading } =
    usePreviousNavigation({
      onSuccess: () => {
        // it will reset value to the latest saved values after pressed 'next step'
        // and clean errors and dirty fields so that when we return on the step again
        // the logic with dirty fields that are being sent to the backend works correctly
        reset(undefined, {
          keepValues: false,
          keepDirty: false,
          keepErrors: false,
        });
      },
    });
  const { mutateAsync: mutateSpecificStep, isPending: isSpecificStepLoading } =
    useSpecificStepNavigation({
      onSuccess: (data, variables) => {
        const navigationType = variables?.body?.navigationType;
        if (navigationType === "NEXT") {
          reset(getValues());
        } else {
          reset(undefined, {
            keepValues: false,
            keepDirty: false,
            keepErrors: false,
          });
        }
      },
    });

  const { mutateAsync: mutateApiCall, isPending: isApiCallLoading } =
    useApiCall({
      onSuccess: async (data, _variables, context) => {
        console.log("STEP_PAGE useApiCall", {
          data,
          variables: _variables,
          context,
        });
        const variables = data.contextInstance.variables;
        if (variables) {
          const formValues = variables.reduce(
            (
              acc: Record<string, any>,
              {
                key,
                value,
                dataType,
              }: {
                key: string;
                value: string;
                dataType: DataTypeEnum;
              },
            ) => {
              acc[key] = safeParseJson(value, dataType);
              return acc;
            },
            {},
          );
          // scrollToTop();
          reset({ ...getValues(), ...formValues }, { keepDirty: true });
        }
      },
    });

  const { mutate: addError } = useMutation({
    mutationFn: addErrorLog,
  });

  const { mutateAsync: updateProcessInstance } = useUpdateProcessInstance();

  const { getTimeSpent } = useTimeSpentStore();

  const getVariable = useContextStore((state) => state.getVariable);
  const replaceVariablesObject = useContextStore((state) => state.replaceVariablesObject);

  const getContextVariablesToUpdate = (
    formData: Record<string, ContextInstanceVariableType["value"]>,
    dirtyFields: Record<string, boolean>,
  ) => {
      return Object.keys(dirtyFields || {}).reduce(
      (acc, key) => {
        const dirtyFieldValue = dirtyFields[key];
        const contextVariableToUpdate = getVariable(key);
        if (
          dirtyFieldValue &&
          contextVariableToUpdate &&
          has(formData, key) &&
          contextVariableToUpdate.readOnly === false
        ) {
          const newValue = parseContextVariableValue(formData[key], {
            ...contextVariableToUpdate,
            dataType: contextVariableToUpdate.dataType,
            key,
          });
          if (isString(newValue)) {
            acc.push({
              ...contextVariableToUpdate,
              value: newValue,
            });
          }
        }

        return acc;
      },
      [] as ContextInstanceVariableType[],
    );
  };

  const getAllContextVariablesToUpdate = (
    formData: Record<string, ContextInstanceVariableType["value"]>,
  ) => {
      return  Object.keys(formData || {}).reduce(
      (acc, key) => {
        const contextVariableToUpdate = getVariable(key);
        if (contextVariableToUpdate) {
          const newValue = parseContextVariableValue(formData[key], {
            dataType: contextVariableToUpdate.dataType,
            key,
          });
          if (isString(newValue)) {
            acc.push({
              ...contextVariableToUpdate,
              value: newValue,
            });
          }
        }
        return acc;
      },
      [] as ContextInstanceVariableType[],
    );
  };

  const handlePrev = async () => {
    if (processInstanceId) {
      await mutatePrev({
        processInstanceId,
        timeSpent: getTimeSpent(),
      });
    }
  };

  const handleNext = async (
    formData: Record<string, ContextInstanceVariableType["value"]>,
  ) => {
    if (processInstanceId) {
      const recordData = formDataMiddleware(null, formData);
      const payload = {
        processInstanceId,
        contextVariablesToUpdate: getContextVariablesToUpdate(
          recordData,
          getKeysToUpdate(),
        ),
        timeSpent: getTimeSpent(),
      };
      await mutateNext(payload);
    }
  };

  const handleSpecificStep = async (
    formData: any,
    params: { stepId: string; navigationType: "NEXT" | "PREVIOUS" },
  ) => {
    const { stepId, navigationType } = params;
    if (processInstanceId) {
      const recordData = formDataMiddleware(null, formData);
      await mutateSpecificStep({
        code: stepId,
        body: {
          processInstanceId,
          contextVariablesToUpdate: getContextVariablesToUpdate(
            recordData,
            getKeysToUpdate(),
          ),
          timeSpent: getTimeSpent(),
          navigationType,
        },
      });
    }
  };

  const handleApiCall = async (
    formData: Record<string, unknown>,
    params: {
      actionName: string;
      actionParams: any[];
      mapper: Record<string, string | null>;
    },
  ) => {
    console.log("api call formData", formData);
    const { actionName, actionParams, mapper } = params;

    if (actionName.toLowerCase().includes("lead") && processInstanceId) {
      addError({
        processInstanceId,
        actionName: "frontendCallLead",
        message: `Lead action is ${actionName}`,
        statusCode: 200,
        requestMethod: "POST",
      });
    }

    if (processInstanceId && processInstance) {
      const recordData = formDataMiddleware(actionName, formData);
      const apiCallVariables = getContextVariablesToApiCall({
        formData: recordData,
        params: actionParams,
        mapper,
        keyToUpdate: getKeysToUpdate(),
      });
      try {
        await mutateApiCall({
          actionName,
          body: {
            id: processInstance.id,
            processId: processInstance.processId,
            activeStepId: processInstance.activeStepId,
            contextInstance: {
              id: processInstance.contextInstance.id,
              variables: apiCallVariables,
            },
          },
        });
      } catch (e) {
        logError(`[Action] handleApiCall error in ${actionName}`, e);
        throw e;
      }
    }
  };

  const isMethodPresent = (
    methods: any[],
    methodName: string,
    strict = true,
  ) => {
    return some(methods, (method) =>
      strict
        ? method?.method === methodName
        : method?.method.includes(methodName),
    );
  };

  // Defense-in-depth premium guard. Blocks navigation when the top-level `premium` has gone
  // stale relative to the freshly recalculated `${pickedCategory}_premium` (the cause of the
  // premium-undercharge defect). Runs before every `navigation_next` on a travel flow, so it
  // protects steps 4 & 5 regardless of whether the DB-configured method chain lists
  // `travel_check_premium`. A no-op for non-travel flows and for a consistent premium.
  const assertTravelPremiumConsistency = () => {
    const mismatch = getTravelPremiumMismatch(getValues());
    if (!mismatch) {
      return;
    }

    addError({
      processInstanceId: processInstanceId ?? "unknown",
      actionName: "premiumMismatch",
      message: `Premium mismatch: ${JSON.stringify(mismatch)}`,
      statusCode: 400,
      requestMethod: "POST",
      requestMessage: JSON.stringify(getValues()),
    });
  };

  const handleClick = async () => {
    actionRunningStore.getState().setIsActionRunning(true);

    try {
      analytics && callAnalyticEvent(analytics);
      setLoadingAction(true);
      if (withActionsLoader) {
        openLoadingModal({
          title: actionsLoaderTitle ?? "",
          description: actionsLoaderDescription ?? "",
          disableClose: true,
        });
      }
        const submit = async () => {
            const clonedValues = structuredClone(getValues());
            await runBeforeSubmitActions(clonedValues);

            if (!Object.values(control._formState.errors).length) {
                const isValid = await trigger();
                await handleSubmit(() => {})();
                if (!isValid) {
                    scrollToTop(true);
                }
                return isValid;
            } else {
                const isValid = await trigger();
                if (!isValid) {
                    scrollToTop(true);
                }
                return false;
            }
        };

      if (
        isMethodPresent(methods, "travelPlansCostCalculator", false) ||
        isMethodPresent(methods, "visaCentreTariffCalculator", false)
      ) {
        const isValid = await trigger();
        console.log("FormError: ", errors);
        if (!isValid) {
          scrollToTop(true);
          return;
        }
      }
      if (isMethodPresent(methods, "documents_approve")) {
        await updateField("issueDateOfContract", nowInKyiv().toISO());
        await handleSubmit(
          (...data) => {
            console.log("SUBMIT SUCCESS", data);
          },
          (...data) => {
            scrollToTop(true);
            console.log("SUBMIT ERROR", data);
          },
        )();
      }
      const prevPremium = getValues("finalPremium");
      setPremium(prevPremium);

      const tourists =
        cleanTouristListFromShadowItems(getValues(FOR_TOURISTS_CONST)) ??
        [];

      const flowCode = getValues("flowCode");

        for (const method of methods) {
        const methodName = method.method;
        const params = method.params;

        if (TRAVEL_FAMILY_FLOW_CODES.includes(flowCode)) {
           void logTravelAction({
            actionName: methodName === "apicall" ? (params?.actionName ?? methodName) : methodName,
            processInstanceId: processInstanceId ?? "unknown",
            url: typeof window !== "undefined" ? window.location.href : "",
            context: flattenContextForLog(getValues()),
          });
        }

        switch (methodName){
            case "force_submit": {
                const isValid = await submit();
                if (!isValid) return;
                break;
            }

            case "navigation_next": {
                assertTravelPremiumConsistency();
                const isValid = await submit();
                if (isValid) {
                    await handleNext(getValues());
                }
                break;
            }

            case "navigation_prev" : {
                await handlePrev();
                break;
            }

            case "navigation_step": {
                const navigationType = params.navigationType;
                if (navigationType === "NEXT") {
                    const isValid = await submit();
                    if (isValid) {
                        await handleSpecificStep(getValues(), params);
                    }
                    return;
                }
                await handleSpecificStep(getValues(), params);
                break;
            }

            case "apicall": {
                await trigger();
                let allowCall = !size(formState.errors);

                if(Object.values(leadActionNameMap).includes(params?.actionName as ApiCallsTypes)) {
                    setValue('phoneModalShouldBeOpened', false);
                    await updateField('phoneModalShouldBeOpened', false);
                }
                // All actions that are related to Lead should be allowed to call if the auto need PVI or not, and also after the unsuccessful condition after the need for PVI.
                if (
                    [
                        "validatePromocode",
                        "AutozahistLeadAction",
                        "CheckNeedPviAction",
                        "AutozahistTariffCalculator",
                        "CalculateFinmonPremium",
                    ].some((item) => item.includes(params?.actionName)) &&
                    formState.errors?.is_vehicle_allowed
                ) {
                    allowCall = true;
                }
                if (allowCall) {
                    params.actionName in apiCallsSideEffects
                        ? await handleApiCallWithSideEffects(
                            getValues(),
                            params,
                            method?.additionalParams,
                        )
                        : await handleApiCall(getValues(), params);
                } else {
                    console.error(`[Action] apicall "${params?.actionName}" skipped due to form validation failure`);
                    addError({
                        processInstanceId: processInstanceId ?? "unknown",
                        actionName: "apicallSkipped",
                        message: `apicall "${params?.actionName}" skipped: form validation failed. Errors: ${JSON.stringify(Object.keys(formState.errors || {}))}`,
                        statusCode: 400,
                        requestMethod: "POST",
                    });
                    scrollToTop(true);
                    // Abort entire method sequence to prevent continuing with stale data
                    return;
                }
                break;
            }

            case "tourists_check": {
                await trigger();
                const isValid = !size(formState.errors);
                const touristsList = (
                    tourists as TouristType[]
                ).map((tourist) =>
                    Object.fromEntries(
                        Object.entries(tourist).map(([key, value]) => [
                            key.replace("tourist_", ""),
                            value,
                        ]),
                    ),
                );
                const touristFields = Object.keys(touristsList[0]).concat([
                    "middleName",
                ]);
                const initialValues = getValues(touristFields);

                const finapCheck = () => {
                    return touristsList.map((tourist) =>
                        handleApiCallByName(ApiCallsTypes.PERFORM_FINAP_ACTION, false, {
                            formDataOverride: {
                                ...getValues(),
                                ...tourist,
                                middleName: null,
                                forTourists: tourists,
                            },
                        }, true),
                    );
                };
                const ordloCheck = () => {
                    return touristsList.map((tourist) =>
                        handleApiCallByName(ApiCallsTypes.CHECK_ORDLO, false, {
                            formDataOverride: {
                                ...getValues(),
                                ...tourist,
                                forTourists: tourists,
                            },
                            mapper: {
                                checkORDLOCode: "city",
                            },
                            paramsOverride: [
                                {
                                    key: "checkORDLOCode",
                                    options: {
                                        type: "STRING",
                                        required: true,
                                        isArray: false,
                                    },
                                },
                            ],
                        }, true),
                    );
                };
                const blackListCheck = () => {
                    return touristsList.map((tourist) =>
                        handleApiCallByName(ApiCallsTypes.CHECK_BLACK_LIST, false, {
                            formDataOverride: {
                                ...getValues(),
                                ...tourist,
                                forTourists: tourists,
                            },
                            mapper: { givenName: "firstName", surname: "lastName" },
                        }, true),
                    );
                };
                if (isValid) {
                    const res = await Promise.allSettled(finapCheck());
                    const res2 = await Promise.allSettled(ordloCheck());
                    const res3 = await Promise.allSettled(blackListCheck());
                    console.log("res, res2, res3", res, res2, res3);
                    if (
                        res.some((r) => r.status === "rejected") ||
                        res2.some((r) => r.status === "rejected") ||
                        res3.some((r) => r.status === "rejected")
                    ) {
                        const insurerData = touristFields.reduce(
                            (acc, key, i) => {
                                const value = initialValues[i];
                                if (value === undefined) {
                                    return acc;
                                }

                                return [...acc, { key, value }];
                            },
                            [] as { key: string; value: any }[],
                        );
                        for (const { key, value } of insurerData) {
                            updateField(key, value);
                        }
                        return;
                    }
                }
                break;
            }

            case "travel_check_premium": {
                assertTravelPremiumConsistency();
                break;
            }

            case "updateIssueDateOfContract":{
                await updateField("issueDateOfContract", nowInKyiv().toISO());
                break;
            }

            case "travelPlansCostCalculator":
            case "travelPlansCostCalculatorRecalc": {
                const isRecalc = methodName === "travelPlansCostCalculatorRecalc";
                const calculateTravelPlans = async () => {
                    // Re-read tourists from current form state to avoid stale closure
                    const currentTourists =
                      cleanTouristListFromShadowItems(getValues(FOR_TOURISTS_CONST)) ?? [];

                    const resolvedInsuredTourists: Record<string, number> = isRecalc
                        ? recalcInsuredCounts(currentTourists)
                        : getValues(INSURED_TOURISTS_CONST);
                    if (!isObject(resolvedInsuredTourists)) {
                        openErrorModal({
                            title: "Сталася помилка",
                            description: "Не вдалося визначити дані застрахованих туристів.\n" +
                                "Будь ласка, перевірте інформацію про туристів та спробуйте ще раз.",
                            submitText: "Закрити",
                            onSubmit: () => {
                                closeErrorModal();
                            }
                        });
                        return;
                    }

                    const recordData = formDataMiddleware(null, getValues());

                    const apiCallActionParams = {
                        countryTravelZone: recordData[COUNTRY_TRAVEL_ZONE_CONST],
                        work: recordData[WORK_CONST],
                        daysAbroad:
                            recordData["travelType"]?.value === "one-time"
                                ? toNumber(recordData["daysAbroad-oneTimeTrip"])
                                : toNumber(recordData["daysAbroad-multiTimeTrip"]?.value),
                        terrorism: recordData[TERRORISM_CONST],
                        travelPurpose: recordData[TRAVEL_PURPOSE_CONST],
                        placeOfStayAbroad: recordData[PLACE_OF_STAY_ABROAD_CONST],
                    };

                    const ageVariables = INSURED_COUNT_VAR_LIST.reduce<
                        Record<string, number>
                    >((acc, touristKey) => {
                        acc[touristKey] = resolvedInsuredTourists[touristKey];
                        return acc;
                    }, {});
                    const listOfRequests: Array<{ plan: string; response: any }> = [];
                    const premiumsByPlan: Record<string, any> = {};

                    const pickedCategoryAccordingDaysAbroad = determinePickedCategory({
                        category: getValues(PICKED_TRAVEL_CALC_CATEGORY_CONST),
                        daysAbroad: toNumber(apiCallActionParams.daysAbroad),
                    });

                    for (const key in ProposalTableColumnsKeys) {
                        const planName =
                            ProposalTableColumnsKeys[
                                key as keyof typeof ProposalTableColumnsKeys
                                ];

                        const staticValueKey = getColContextValueString(planName);
                        const planNameReqData = getPlanNameReqData(planName, getValues());

                        if (!planNameReqData) {
                            openErrorModal({
                                title: "Сталася помилка",
                                description: "Шановний клієнте, під час обробки вашого замовлення сталась технічна помилка.\n" +
                                    "Будь ласка, зателефонуйте за контактами інтернет-магазину або скористайтесь опцією «Написати нам».\n" +
                                    "Перепрошуємо за незручності та дякуємо за розуміння!",
                                submitText: "Написати",
                                onSubmit: () => {
                                    closeErrorModal();
                                    useTextUsModalStore.getState().open();
                                }
                            });
                            throw new Error("Plan name not found");
                        }

                        const variables = {
                          ...planNameReqData,
                          [staticValueKey]: planNameReqData,
                          ...apiCallActionParams,
                          ...ageVariables,
                          calculationCategory: planName,
                          pickedTravelCalculationCategory:
                            pickedCategoryAccordingDaysAbroad,
                          [FOR_TOURISTS_CONST]: currentTourists,
                          insuredCount: sum(Object.values(resolvedInsuredTourists)),
                          insuredTourists: resolvedInsuredTourists,
                        };

                        const formattedVariables = formatFormDataToVarList(variables);

                        const requestBody: any = {
                            id: processInstanceId,
                            processId: processInstance.processId,
                            activeStepId: processInstance.activeStepId,
                            contextInstance: {
                                id: processInstance.contextInstance.id,
                                variables: formattedVariables,
                            },
                        };

                        try {
                            const response = await runAction({
                                actionName: ApiCallsTypes.TRAVEL_TARIFF_CALCULATOR,
                                body: requestBody,
                            });
                            listOfRequests.push({ plan: planName, response });
                            const planFormValues = formatContextVariablesListToFormValues(
                                response.contextInstance.variables,
                            );
                            premiumsByPlan[`${planName}_premium`] = planFormValues[`${planName}_premium`];
                        } catch (error) {
                            console.error(`Error processing plan: ${planName}`, error);
                            throw new Error("Failed to calculate");
                        }
                    }

                    const latestTariffResponse = last(listOfRequests)?.response;
                    if (!latestTariffResponse) {
                        throw new Error("No response available for processing.");
                    }

                    const formValues = formatContextVariablesListToFormValues(
                        latestTariffResponse.contextInstance.variables,
                    );

                    const formattedKeys = premiumsByPlan;

                    const staticValueKey = pickedCategoryAccordingDaysAbroad
                        ? getColContextValueString(pickedCategoryAccordingDaysAbroad)
                        : "Business_req_data";
                    const columnStaticValues = staticValueKey
                        ? getValues(staticValueKey)
                        : {};

                    const mergedValues = {
                        ...getValues(),
                        ...formValues,
                        ...formattedKeys,
                        ...columnStaticValues,
                        calculationCategory: pickedCategoryAccordingDaysAbroad,
                        pickedTravelCalculationCategory:
                          pickedCategoryAccordingDaysAbroad,
                        [FOR_TOURISTS_CONST]: currentTourists,
                        insuredTourists: resolvedInsuredTourists,
                        insuredCount: sum(Object.values(resolvedInsuredTourists)),
                    };

                    reset(mergedValues, { keepDirty: true });

                    // After reset, explicitly sync premium/finalPremium from the picked category
                    // to prevent stale values when PickTravelTariffCategoryAction runs next
                    const pickedCat = mergedValues.pickedTravelCalculationCategory;
                    if (pickedCat && mergedValues[`${pickedCat}_premium`] != null) {
                        setValue("premium", mergedValues[`${pickedCat}_premium`]);
                        setValue("finalPremium", mergedValues[`${pickedCat}_finalPremium`] ?? mergedValues[`${pickedCat}_premium`]);
                    }
                };
                await calculateTravelPlans();
                break;
            }

            // Mirrors travelPlansCostCalculator/Recalc above for Visa Centre (VZK_NEW).
            case "visaCentreTariffCalculator":
            case "visaCentreTariffCalculatorRecalc": {
                const isRecalc = methodName === "visaCentreTariffCalculatorRecalc";
                const calculateVisaCentrePlans = async () => {
                    const currentTourists =
                      cleanTouristListFromShadowItems(getValues(FOR_TOURISTS_CONST)) ?? [];

                    const resolvedInsuredTourists: Record<string, number> = isRecalc
                        ? recalcInsuredCountsVisaCentre(currentTourists)
                        : getValues(INSURED_TOURISTS_CONST);
                    if (!isObject(resolvedInsuredTourists)) {
                        openErrorModal({
                            title: "Сталася помилка",
                            description: "Не вдалося визначити дані застрахованих туристів.\n" +
                                "Будь ласка, перевірте інформацію про туристів та спробуйте ще раз.",
                            submitText: "Закрити",
                            onSubmit: () => {
                                closeErrorModal();
                            }
                        });
                        return;
                    }

                    const recordData = formDataMiddleware(null, getValues());

                    const apiCallActionParams = {
                        countryTravelZone: getSelectOptionValue(recordData[COUNTRY_TRAVEL_ZONE_CONST]) ?? null,
                        work: recordData[WORK_CONST],
                        daysAbroad:
                            recordData["travelType"]?.value === "one-time"
                                ? toNumber(recordData["daysAbroad-oneTimeTrip"])
                                : toNumber(recordData["daysAbroad-multiTimeTrip"]?.value),
                        terrorism: recordData[TERRORISM_CONST],
                        travelPurpose: recordData[TRAVEL_PURPOSE_CONST],
                        placeOfStayAbroad: recordData[PLACE_OF_STAY_ABROAD_CONST],
                    };

                    const ageVariables = INSURED_COUNT_VAR_LIST_VISA_CENTRE.reduce<
                        Record<string, number>
                    >((acc, touristKey) => {
                        acc[touristKey] = resolvedInsuredTourists[touristKey];
                        return acc;
                    }, {});
                    const listOfRequests: Array<{ plan: string; response: any }> = [];
                    const premiumsByPlan: Record<string, any> = {};

                    const pickedVisaCentreCategory = getValues(PICKED_TRAVEL_CALC_CATEGORY_CONST);

                    for (const key in VisaCentreColumnsKeys) {
                        const planName =
                            VisaCentreColumnsKeys[
                                key as keyof typeof VisaCentreColumnsKeys
                                ];

                        const staticValueKey = getColContextValueString(planName);
                        const planNameReqData = getPlanNameReqData(planName, getValues());

                        if (!planNameReqData) {
                            openErrorModal({
                                title: "Сталася помилка",
                                description: "Шановний клієнте, під час обробки вашого замовлення сталась технічна помилка.\n" +
                                    "Будь ласка, зателефонуйте за контактами інтернет-магазину або скористайтесь опцією «Написати нам».\n" +
                                    "Перепрошуємо за незручності та дякуємо за розуміння!",
                                submitText: "Написати",
                                onSubmit: () => {
                                    closeErrorModal();
                                    useTextUsModalStore.getState().open();
                                }
                            });
                            throw new Error("Plan name not found");
                        }

                        const variables = {
                          ...planNameReqData,
                          [staticValueKey]: planNameReqData,
                          ...apiCallActionParams,
                          ...ageVariables,
                          calculationCategory: planName,
                          pickedTravelCalculationCategory: pickedVisaCentreCategory,
                          [FOR_TOURISTS_CONST]: currentTourists,
                          insuredCount: sum(Object.values(resolvedInsuredTourists)),
                          insuredTourists: resolvedInsuredTourists,
                        };

                        const formattedVariables = formatFormDataToVarList(variables);

                        const requestBody: any = {
                            id: processInstanceId,
                            processId: processInstance.processId,
                            activeStepId: processInstance.activeStepId,
                            contextInstance: {
                                id: processInstance.contextInstance.id,
                                variables: formattedVariables,
                            },
                        };

                        try {
                            const response = await runAction({
                                actionName: ApiCallsTypes.VISA_CENTER_TARIFF_CALCULATOR,
                                body: requestBody,
                            });
                            listOfRequests.push({ plan: planName, response });
                            const planFormValues = formatContextVariablesListToFormValues(
                                response.contextInstance.variables,
                            );
                            premiumsByPlan[`${planName}_premium`] = planFormValues[`${planName}_premium`];
                        } catch (error) {
                            console.error(`Error processing plan: ${planName}`, error);
                            throw new Error("Failed to calculate");
                        }
                    }

                    const latestTariffResponse = last(listOfRequests)?.response;
                    if (!latestTariffResponse) {
                        throw new Error("No response available for processing.");
                    }

                    const formValues = formatContextVariablesListToFormValues(
                        latestTariffResponse.contextInstance.variables,
                    );

                    const formattedKeys = premiumsByPlan;

                    const staticValueKey = pickedVisaCentreCategory
                        ? getColContextValueString(pickedVisaCentreCategory)
                        : null;
                    const columnStaticValues = staticValueKey
                        ? getValues(staticValueKey)
                        : {};

                    const mergedValues = {
                        ...getValues(),
                        ...formValues,
                        ...formattedKeys,
                        ...columnStaticValues,
                        calculationCategory: pickedVisaCentreCategory,
                        pickedTravelCalculationCategory: pickedVisaCentreCategory,
                        [FOR_TOURISTS_CONST]: currentTourists,
                        insuredTourists: resolvedInsuredTourists,
                        insuredCount: sum(Object.values(resolvedInsuredTourists)),
                    };

                    reset(mergedValues, { keepDirty: true });

                    // After reset, explicitly sync premium/finalPremium from the picked category
                    // to prevent stale values when PickTravelTariffCategoryAction runs next
                    const pickedCat = mergedValues.pickedTravelCalculationCategory;
                    if (pickedCat && mergedValues[`${pickedCat}_premium`] != null) {
                        setValue("premium", mergedValues[`${pickedCat}_premium`]);
                        setValue("finalPremium", mergedValues[`${pickedCat}_finalPremium`] ?? mergedValues[`${pickedCat}_premium`]);
                    }
                };
                await calculateVisaCentrePlans();
                break;
            }

            case "documents_approve": {
                const isValid = await submit();
                const datesValid = !!getValues("startDate") && !!getValues("endDate");

                documentsApprove(isValid, datesValid, processInstanceId, getSearchParam(PROCESS_INSTANCE_QUERY), method);
                break;
            }

            case "crosses_check": {
                const isValid = await submit();
                const pickedCrosses = getValues("pickedCrosses");
                const isLegkiyCrossAllowed =
                    getValues("vehicleType")?.value?.includes("B");
                crossesCheck(isValid, pickedCrosses, isLegkiyCrossAllowed, router);
                break;
            }

            case "check_existing_contracts": {
                try {
                    const pdfLink = method.additionalParams.pdfLink;
                    await checkExistingContracts(!size(formState.errors), getValues('contractsForCancellation'), pdfLink);
                } catch (e) {
                    logError('[Action] Error in check_existing_contracts', e);
                    console.error(e);
                    return;
                }
                break;
            }

            case "download_policy": {
                const policyPrintouts = getValues("policyPrintouts")?.filter(Boolean);
                const contractNumber = getValues("contractNumber");

                await downloadPolicy(policyPrintouts, contractNumber.toString());
                break;
            }

            case "check_premium_limit": {
                const finalPremium = getValues("finalPremium") || 0;
                checkPremiumLimit(Number(finalPremium), trackAnalyticEvent);
                break;
            }
        }
      }
      onClick?.();
    } catch (error) {
      logError('[Action] handleClick error', error);
      setLoadingAction(false);
      console.error("An error occurred during sequential execution:", error);
    } finally {
      if (withActionsLoader) {
        closeLoadingModal();
      }
      setLoadingAction(false);
      actionRunningStore.getState().setIsActionRunning(false);

      console.log("ALL REQUESTS ARE DONE");
    }
  };

  const params = methods?.[0]?.params;
  const orderStatus = processInstance?.order?.status;

  return (
    <ButtonUI
      {...restProps}
      onClick={handleClick}
      loading={
        isNextLoading ||
        isPrevLoading ||
        isSpecificStepLoading ||
        isApiCallLoading ||
        isLoadingAction
      }
      href={params?.href}
      target={params?.target}
      disabled={
        restProps.disabled ||
        isSelectionPending ||
        (isActionRunning && isMethodPresent(methods, "navigation_", false)) ||
        ((isMethodPresent(methods, "documents_approve") ||
          isMethodPresent(methods, "navigation_", false)) &&
          ["BILLING_IN_PROGRESS", "ERROR"].includes(orderStatus))
      }
    >
      <ParsedValue value={children as string} maskType={restProps?.maskType} />
    </ButtonUI>
  );
}
Action.displayName = "Button";
